import { generateRadioButtonOptionsFromEnum } from 'components/final-form/radioUtils';
import React, { Component } from 'react';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Header, RadioProps } from 'semantic-ui-react';
import { Field, Form as FinalForm, FormRenderProps, FormSpy } from 'react-final-form';
import Radio from 'components/final-form/Radio';
import Input from 'components/final-form/Input';
import DatePicker from 'components/final-form/DatePicker';
import { BillSearchResponse, BillStornoSearchRequest, PatientDto } from 'ts-types/api.types';
import { ensureOnlyNumericInput } from 'utils/filterUtils';
import moment from 'moment';
import createDecorator, { Calculation } from 'final-form-calculate';
import { debounce } from 'ts-debounce';
import { FormApi, FormState } from 'final-form';
import { isValueEmpty } from 'utils/validatorUtils';
import { BillSearchBy, StornoType } from 'ts-types/api.enums';
import { CancelTokenSource } from 'axios';
import axios from 'services/http';
import { searchStornoBills } from 'services/stornoBillServices';
import styled from 'styled-components';
import { withRouterWorkaround } from 'utils/workaroundUtils';
import { RouteComponentProps } from 'react-router';

const StyledHeader = styled(Header)`
  margin-bottom: 1em !important;
`;

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  padding: 1em 1em;

  .ui.buttons {
    margin-left: 10px !important;
  }

  .field {
    display: inline-block !important;
    min-width: 12rem;
  }
`;

const StyledContainer = styled.div`

  flex: 1 1 auto;

  .ui.header:not(h1):not(h2):not(h3):not(h4):not(h5):not(h6) {
    font-size: 1.1rem;
    color: #373737;

    &.small {
      font-size: 1.05em;
    }
  }
`;

const StyledLabel = styled.label`
  display: inline-block;
  min-width: 40px !important;
  margin-right: 10px;

  &.margin + .margin {
    margin-left: 10px;
  }
`;

const SearchOptionsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
`;

const SearchGroup = styled.div`
  flex: 1 1 20%;
`;

const SearchField = styled.div`
  margin-bottom: 7px;
`;

const StyledSpan = styled.span`
  font-weight: bold;
  padding-left: 1.5rem;
`;

interface Props extends RouteComponentProps<any>, MessageSourceProps {
  onStornoTypeChange: (stornoType: StornoType) => void,
  onFilterBills: (bills: Array<BillSearchResponse>) => void,
  onBillsLoadingChange: (billsLoading: boolean) => void,
  currentStornoType: StornoType,
  currentPatient?: PatientDto
}

interface ExtendedBillStornoSearchRequest extends BillStornoSearchRequest {
  recentlyChanged: boolean
}

let initialStornoValues: Partial<ExtendedBillStornoSearchRequest> = {
  searchBy: BillSearchBy.BILL_NUMBER,
  fromDate: moment().format("YYYY-MM-DD"),
  toDate: moment().format("YYYY-MM-DD"),
  recentlyChanged: false
}

class BillStornoSearchView extends Component<Props> {

  radioMap: Map<string, RadioProps> = new Map<string, RadioProps>();
  cancelTokenSource: CancelTokenSource = axios.CancelToken.source();

  constructor(props: Props) {
    super(props);

    const {radioMap} = this;
    const {currentPatient, currentStornoType, getMessage} = props;

    const state: any = this.props.location.state;
    const selectedBillId = (state && state.selectedBill) ? state.selectedBill.id : undefined;

    const searchByValues = Object.values(BillSearchBy);
    const stornoTypeValues = Object.values(StornoType);

    const radioDefinitions = generateRadioButtonOptionsFromEnum(searchByValues, "searchBy", getMessage);
    const stornoTypesRadioDefinitions = generateRadioButtonOptionsFromEnum(stornoTypeValues, "stornoType", getMessage);

    radioDefinitions.forEach((radioDefinition, index) => {
      radioMap.set(searchByValues[index], radioDefinition);
    });

    stornoTypesRadioDefinitions.forEach((radioDefinition, index) => {
      radioMap.set(stornoTypeValues[index], radioDefinition);
    });

    if (selectedBillId) {
      initialStornoValues.searchBy = BillSearchBy.BILL_NUMBER;
      initialStornoValues.billNumber = selectedBillId;
    } else if (currentPatient) {
      initialStornoValues.searchBy = BillSearchBy.CURRENT_PATIENT;
      initialStornoValues.patientId = currentPatient.id;
      initialStornoValues.billNumber = undefined;
    }

    initialStornoValues.stornoType = currentStornoType === StornoType.BILL ? StornoType.BILL : StornoType.PAYMENT;
  }

  shouldResetSearchBy = (searchBy: BillSearchBy, params: Array<any>): Partial<ExtendedBillStornoSearchRequest> => {

    const allValues: Partial<ExtendedBillStornoSearchRequest> = params[2];
    const prevValues: Partial<ExtendedBillStornoSearchRequest> = params[3];

    // `Object.entries(prevValues).length !== 0` is here to prevent decorator being called when the form is being initialized
    if (searchBy != prevValues.searchBy && !allValues.recentlyChanged && Object.entries(prevValues).length !== 0) {
      return this.resetSearchBy(searchBy);
    }

    return {
      recentlyChanged: false
    }
  };

  resetSearchBy = (searchBy: BillSearchBy): Partial<BillStornoSearchRequest> => {

    const defaultValues = {
      searchBy: searchBy,
      recentlyChanged: true
    };

    switch (searchBy) {
      case BillSearchBy.BILL_NUMBER: {
        return {
          ...defaultValues,
          fromDate: moment().format("YYYY-MM-DD"),
          toDate: moment().format("YYYY-MM-DD"),
          fromAmount: undefined,
          toAmount: undefined
        }
      }
      case BillSearchBy.CURRENT_PATIENT: {
        return {
          ...defaultValues,
          billNumber: undefined,
          fromDate: moment().format("YYYY-MM-DD"),
          toDate: moment().format("YYYY-MM-DD"),
          fromAmount: undefined,
          toAmount: undefined
        }
      }
      case BillSearchBy.AMOUNT: {
        return {
          ...defaultValues,
          billNumber: undefined,
          fromDate: moment().format("YYYY-MM-DD"),
          toDate: moment().format("YYYY-MM-DD")
        }
      }
      case BillSearchBy.DATE: {
        return {
          ...defaultValues,
          billNumber: undefined,
          fromAmount: undefined,
          toAmount: undefined
        }
      }
      default: {
        return {};
      }
    }
  };

  constructCalculation = (fieldName: string, searchBy?: BillSearchBy): Calculation => {
    if (!searchBy) {
      return {
        field: fieldName,
        updates: (...params) => this.shouldResetSearchBy(params[0], params)
      }
    }

    return {
      field: fieldName,
      updates: (...params) => this.shouldResetSearchBy(searchBy, params)
    }
  };

  searchBy = createDecorator(
      this.constructCalculation("billNumber", BillSearchBy.BILL_NUMBER),
      this.constructCalculation("fromAmount", BillSearchBy.AMOUNT),
      this.constructCalculation("toAmount", BillSearchBy.AMOUNT),
      this.constructCalculation("fromDate", BillSearchBy.DATE),
      this.constructCalculation("toDate", BillSearchBy.DATE),
      this.constructCalculation("searchBy")
  );

  shouldSearchBills = (values: Partial<ExtendedBillStornoSearchRequest>): boolean => {

    const {searchBy, billNumber, fromAmount, toAmount} = values;

    switch (searchBy) {
      case BillSearchBy.BILL_NUMBER: {
        return !isValueEmpty(billNumber);
      }
      case BillSearchBy.AMOUNT: {
        return !isValueEmpty(fromAmount) && !isValueEmpty(toAmount);
      }
      default: {
        return true;
      }
    }
  };

  handleChange = ({values}: FormState, form: FormApi) => {

    const {currentStornoType, onStornoTypeChange, onBillsLoadingChange} = this.props;

    const fromDate = values.fromDate && new Date(values.fromDate);
    const toDate = values.toDate && new Date(values.toDate);

    if (values.stornoType && currentStornoType != values.stornoType) {
      onStornoTypeChange(values.stornoType);
    }

    if (this.shouldSearchBills(values)) {
      onBillsLoadingChange(true);
      this.searchBills(values);
    }

    if (fromDate && toDate && fromDate.getTime() > toDate.getTime()) {
      form.change("toDate", moment(fromDate).format("YYYY-MM-DD"));
    }
  };

  searchBills = debounce((values: Partial<ExtendedBillStornoSearchRequest>) => {

    const {onBillsLoadingChange, onFilterBills} = this.props;

    searchStornoBills(values, this.cancelTokenSource)
        .then(response => {
          onFilterBills(response);
        })
        .finally(() => {
          onBillsLoadingChange(false);
        })
  }, 400);

  render(): React.ReactNode {
    return (
        <FinalForm
            onSubmit={() => {
            }}
            decorators={[this.searchBy]}
            initialValues={initialStornoValues}
            render={this.renderForm}
        />
    );
  }

  renderForm = ({handleSubmit, values, form}: FormRenderProps): React.ReactNode => {

    const {radioMap, props} = this;
    const {getMessage, currentPatient} = props;

    const dateRadioDefinition = radioMap.get(BillSearchBy.DATE);
    const amountRadioDefinition = radioMap.get(BillSearchBy.AMOUNT);
    const billNumberRadioDefinition = radioMap.get(BillSearchBy.BILL_NUMBER);
    const currentPatientRadioDefinition = radioMap.get(BillSearchBy.CURRENT_PATIENT);

    const billStornoRadioDefinition = radioMap.get(StornoType.BILL);
    const paymentStornoRadioDefinition = radioMap.get(StornoType.PAYMENT);

    const disabledBefore = values.fromDate && new Date(values.fromDate);

    return (
        <StyledForm onSubmit={handleSubmit}>
          <StyledContainer>
            <StyledHeader>{getMessage("searchOptions")}</StyledHeader>
          </StyledContainer>
          <StyledContainer>
            <SearchOptionsContainer>
              <SearchGroup>
                <SearchField>
                  <Field
                      name="searchBy"
                      component={Radio}
                      radioDefinition={billNumberRadioDefinition}
                  />
                </SearchField>
                <SearchField>
                  <StyledLabel className="margin">
                    {getMessage("from")}:
                  </StyledLabel>
                  <Field
                      name="billNumber"
                      component={Input}
                      onKeyDown={ensureOnlyNumericInput}
                      placeholder={getMessage("searchBy.BILL_NUMBER")}
                      fluid
                  />
                </SearchField>
              </SearchGroup>
              <SearchGroup>
                <SearchField>
                  <Field
                      name="searchBy"
                      component={Radio}
                      disabled={!currentPatient}
                      radioDefinition={currentPatientRadioDefinition}
                  />
                </SearchField>
                <SearchField>
                  {currentPatient &&
                  <StyledSpan>
                    {`${currentPatient.name} ${currentPatient.firstName}`}
                  </StyledSpan>
                  }
                </SearchField>
              </SearchGroup>
              <SearchGroup>
                <SearchField>
                  <Field
                      name="searchBy"
                      component={Radio}
                      radioDefinition={amountRadioDefinition}
                  />
                </SearchField>
                <SearchField>
                  <StyledLabel className="margin">
                    {getMessage("from")}:
                  </StyledLabel>
                  <Field
                      name="fromAmount"
                      component={Input}
                      onKeyDown={ensureOnlyNumericInput}
                      placeholder={getMessage("fromAmount")}
                      fluid
                  />
                </SearchField>
                <SearchField>
                  <StyledLabel className="margin">
                    {getMessage("to")}:
                  </StyledLabel>
                  <Field
                      name="toAmount"
                      component={Input}
                      onKeyDown={ensureOnlyNumericInput}
                      placeholder={getMessage("toAmount")}
                      fluid
                  />
                </SearchField>
              </SearchGroup>
              <SearchGroup>
                <SearchField>
                  <Field
                      name="searchBy"
                      component={Radio}
                      radioDefinition={dateRadioDefinition}
                  />
                </SearchField>
                <SearchField>
                  <StyledLabel className="margin">
                    {getMessage("from")}:
                  </StyledLabel>
                  <Field
                      name="fromDate"
                      component={DatePicker}
                      fluid
                  />
                </SearchField>
                <SearchField>
                  <StyledLabel className="margin">
                    {getMessage("to")}:
                  </StyledLabel>
                  <Field
                      name="toDate"
                      component={DatePicker}
                      disabledBefore={disabledBefore}
                      fluid
                  />
                </SearchField>
              </SearchGroup>
              <SearchGroup>
                <Header as="h4">
                  {getMessage("stornoType")}
                </Header>
                <SearchField>
                  <Field
                      name="stornoType"
                      component={Radio}
                      radioDefinition={billStornoRadioDefinition}
                  />
                </SearchField>
                <SearchField>
                  <Field
                      name="stornoType"
                      component={Radio}
                      radioDefinition={paymentStornoRadioDefinition}
                  />
                </SearchField>
              </SearchGroup>
            </SearchOptionsContainer>
          </StyledContainer>
          <FormSpy subscription={{values: true}} onChange={formState => this.handleChange(formState, form)}/>
        </StyledForm>
    )
  }
}

export default withRouterWorkaround(withMessages(BillStornoSearchView));
